import { useEffect, useRef, useState } from "react";
import { ArrowUpDown, Check } from "lucide-react";
import { FORM_CONTROL } from "shared/tableStyles";

export type SortDir = "asc" | "desc";

export interface SortOption<K extends string> {
  key: K;
  label: string;
}

interface Props<K extends string> {
  options: SortOption<K>[];
  /** The active sort field, or "" for the default order. */
  sortBy: K | "";
  sortDir: SortDir;
  onChange: (sortBy: K | "", sortDir: SortDir) => void;
}

// Two single-select columns: what to sort by (left) and ASC / DESC (right).
export default function SortMenu<K extends string>({ options, sortBy, sortDir, onChange }: Props<K>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = options.find((o) => o.key === sortBy);
  const item = (selected: boolean) =>
    `flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${selected ? "text-primary font-medium" : "text-foreground"}`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-1.5 h-9 ${FORM_CONTROL}`}
      >
        <ArrowUpDown className="w-3.5 h-3.5" />
        {active ? `${active.label} · ${sortDir === "asc" ? "ASC" : "DESC"}` : "Sort"}
      </button>
      {open && (
        <div role="dialog" aria-label="Sort" className="absolute right-0 top-full z-30 mt-1 flex rounded-md border border-border bg-surface p-2 shadow-lg">
          <div className="min-w-[190px] pr-2 border-r border-border" role="group" aria-label="Sort by">
            {options.map((o) => (
              <button key={o.key} type="button" aria-pressed={o.key === sortBy} className={item(o.key === sortBy)} onClick={() => onChange(o.key === sortBy ? "" : o.key, sortDir)}>
                {o.label}
                {o.key === sortBy && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
          <div className="min-w-[90px] pl-2" role="group" aria-label="Order">
            {(["asc", "desc"] as SortDir[]).map((d) => (
              <button key={d} type="button" aria-pressed={d === sortDir} className={item(d === sortDir)} onClick={() => onChange(sortBy, d)}>
                {d === "asc" ? "ASC" : "DESC"}
                {d === sortDir && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
